import { useQuery } from '@tanstack/react-query'
import { useAppSelector } from '@/hooks'
import { selectTenant } from '@/features/auth/authSlice'
import { fetchIntegrationConnections } from '@/api/modules/integrations'
import { normalizeData } from '@/utils/jsonapi'

/** React Query key namespace for the integration connections list (FE-4). */
export const INTEGRATION_CONNECTIONS_QUERY_KEY = 'integration-connections'

/**
 * Fetch integration connections (flattened from their JSON:API envelope),
 * for surfaces that need to check *which* connections exist/qualify rather
 * than read one connection's own fields (that's FE-2's singular
 * `useIntegrationConnection`). Built for the metric wizard's "Start from
 * Rock" entry, which needs to know whether any linked Rock connection has
 * `capability_snapshot.metrics` - the same filter shape (`filter[provider]`,
 * `filter[status]`) `IntegrationConnectionViewSet.filterset_fields` already
 * supports (apps/integrations/views.py, landed on develop).
 *
 * Tenant-keyed and gated the same way as every other hook in this plan.
 *
 * @param {Object} [queryOptions] - passed straight through to
 *   `fetchIntegrationConnections` (buildJsonApiQuery's `{filters, sort, ...}` shape)
 * @returns {import('@tanstack/react-query').UseQueryResult<Array<Object>>}
 */
export function useIntegrationConnections(queryOptions = {}) {
  const tenantId = useAppSelector(selectTenant)?.id

  return useQuery({
    queryKey: [INTEGRATION_CONNECTIONS_QUERY_KEY, tenantId, queryOptions],
    queryFn: async () => {
      const envelope = await fetchIntegrationConnections(queryOptions)
      return normalizeData(envelope?.data) || []
    },
    enabled: !!tenantId,
  })
}
